import { isAdministeredDose, type ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";

export type OverviewCategoryKey = "lab_result" | "prescription" | "visit_summary" | "imaging" | "immunization";

export interface OverviewListItem {
  id: string;
  title: string;
  detail: string;
  date: string; // ISO yyyy-mm-dd, "" if unknown
}

function firstDateOnly(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.length >= 10) return value.slice(0, 10);
  }
  return "";
}

/** Only resource types with a real, unambiguous bucket are counted here — anything
 * else (Patient, Practitioner, Coverage, Consent, etc.) isn't a "health record" in
 * the sense this chart communicates, so it's left uncategorized rather than forced
 * into one of the 5 columns. */
export function categorizeRecord(record: ParsedRecord): OverviewCategoryKey | null {
  const f = record.fields;
  switch (record.resourceType) {
    case "MedicationRequest":
      return "prescription";
    case "Encounter":
      return "visit_summary";
    case "Immunization":
      return "immunization";
    case "Observation":
      return "lab_result";
    case "DiagnosticReport": {
      const category = String(f.category ?? "").toLowerCase();
      const contentType = String(f.attachment_content_type ?? "").toLowerCase();
      const isImaging = category.includes("imag") || category.includes("radiol") || contentType.includes("image");
      return isImaging ? "imaging" : "lab_result";
    }
    default:
      return null;
  }
}

export function toOverviewListItem(record: ParsedRecord): OverviewListItem {
  const f = record.fields;
  switch (record.resourceType) {
    case "MedicationRequest":
      return {
        id: record.id,
        title: record.displayName || (f.medication_name as string) || "Medication",
        detail: isAdministeredDose(record)
          ? ["Administered", (f.dosage_text as string) || ""].filter(Boolean).join(" · ")
          : (f.dosage_text as string) || (f.frequency as string) || "Prescription",
        date: firstDateOnly(f.authored_on),
      };
    case "Encounter":
      return {
        id: record.id,
        title: (f.encounter_type as string) || (f.encounter_class_display as string) || "Visit",
        detail: (f.practitioner_name as string) || (f.practitioner_display as string) || (f.service_provider as string) || (f.location_display as string) || "Visit",
        date: firstDateOnly(f.start_date),
      };
    case "Immunization":
      return {
        id: record.id,
        title: (f.vaccine_display as string) || "Immunization",
        detail: (f.site as string) || (f.route as string) || "Immunization",
        date: firstDateOnly(f.occurrence_date),
      };
    case "Observation":
      return {
        id: record.id,
        title: (f.display_name as string) || "Observation",
        detail: (f.category as string) || "Lab result",
        date: firstDateOnly(f.effective_date),
      };
    case "DiagnosticReport":
    default:
      return {
        id: record.id,
        title: (f.display_name as string) || "Report",
        detail: (f.category as string) || (f.attachment_content_type as string) || "Report",
        date: firstDateOnly(f.issued_date, f.effective_date),
      };
  }
}
